import { useCallback, useState } from "react";
import intl from "react-intl-universal";
import { Link, Redirect, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";
import { loadItem } from "../../../Utils";
import Login from "../Login";
import VerificationCodeLogin from "../VerificationCode";
import ResetPassword from "../ResetPassword";
import ForgotPassword from "../ForgotPassword";
import { MyFirstAccess } from "../MyFirstAccess";
import { AcceptTerms } from "../AcceptTerms";
import { KycOnboardPage } from "../KycOnboard";
import { useAuth } from "../../../Contexts/AuthContext";
import { PrivacyPolicyModal } from "../Privacy";
import GlobalStyle from "../styles/GlobalStyle";
import { CompanyLogo } from "../../../Components/Atoms/CompanyLogo";
import { LanguageSelector } from "../../../Components/Atoms/LanguageSelector";
import {
  isPartnerBrand,
  loginPageThemeStyle,
  partnerBrandLabel,
  partnerLoginHero,
} from "../loginPartnerTheme";

/** Partner white-label when DNS resolved a non-matrix instance. */
const shouldForceValutLogo = () => {
  const options = loadItem("Options") || {};
  return !isPartnerBrand(options);
};

export const PublicTemplate = () => {
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false);
  const options = loadItem("Options") || {};
  const { site } = options;
  const isPartner = isPartnerBrand(options);
  const brandLabel = partnerBrandLabel(options);
  const forceValutLogo = shouldForceValutLogo();
  const loginThemeStyle = loginPageThemeStyle(options);
  const partnerHero = partnerLoginHero(options);
  const { user } = useAuth();
  const { path } = useRouteMatch();
  const location = useLocation();
  const isLoginRoute =
    location.pathname === path || location.pathname === `${path}/`;
  const isKycOnboardRoute = location.pathname.includes("/onboard/");
  // Login card already shows the brand wordmark; keep mobile mark for other auth steps.
  const showMobileLogo =
    !isLoginRoute && !location.pathname.includes("/verification-code");

  const handleClose = useCallback(() => {
    setOpenPrivacyPolicy(false);
  }, []);

  // Allow public KYC invite redeem even when the browser already has a session.
  if (user.isAutenticated && user.accepted_terms && !isKycOnboardRoute) {
    return <Redirect to="/" />;
  }

  const footer = (
    <footer className="login-page__footer">
      <button
        type="button"
        className="login-page__footer-link"
        onClick={() => site && window.open(site, "_blank", "noopener,noreferrer")}
      >
        {intl.get("words.terms")}
      </button>
      <span className="login-page__footer-sep">·</span>
      <button
        type="button"
        className="login-page__footer-link"
        onClick={() => setOpenPrivacyPolicy(true)}
      >
        {intl.get("words.privacy")}
      </button>
      <span className="login-page__footer-sep">·</span>
      <button
        type="button"
        className="login-page__footer-link"
        onClick={() => site && window.open(site, "_blank", "noopener,noreferrer")}
      >
        {intl.get("words.support")}
      </button>
      <span className="login-page__footer-sep">·</span>
      <span className="login-page__footer-copy">
        © {new Date().getFullYear()} {isPartner ? brandLabel : "Valut"}
      </span>
    </footer>
  );

  // Dedicated centered surface for public KYC onboard (not the split login layout).
  if (isKycOnboardRoute) {
    return (
      <div className="kyc-onboard-page">
        <div className="kyc-onboard-page__grid" aria-hidden="true" />
        <div className="kyc-onboard-page__glow" aria-hidden="true" />

        <div className="kyc-onboard-page__toolbar">
          <LanguageSelector className="kyc-onboard-page__language" />
        </div>

        <main className="kyc-onboard-page__main">
          <Route path={`${path}/onboard/:token`}>
            <KycOnboardPage />
          </Route>
        </main>

        {footer}
        <GlobalStyle />
        <PrivacyPolicyModal open={openPrivacyPolicy} onClose={handleClose} />
      </div>
    );
  }

  return (
    <div
      className="login-page"
      data-partner-brand={isPartner ? "true" : undefined}
      style={loginThemeStyle}
    >
      <div className="login-page__grid" aria-hidden="true" />

      <aside className="login-page__brand">
        <div className="login-page__brand-glow" aria-hidden="true" />

        <Link to="/auth" className="login-page__wordmark" aria-label={brandLabel}>
          <CompanyLogo
            forceValut={forceValutLogo}
            onDark
            variant="gold"
            width={220}
            height={40}
          />
        </Link>

        {isPartner && partnerHero.headline ? (
          <div className="login-page__brand-content login-page__brand-content--partner">
            <h2 className="login-page__headline login-page__headline--partner">
              {partnerHero.headline}
            </h2>

            <div className="login-page__divider" />

            {partnerHero.description ? (
              <p className="login-page__description login-page__description--partner">
                {partnerHero.description}
              </p>
            ) : null}
          </div>
        ) : !isPartner ? (
          <div className="login-page__brand-content">
            <div className="login-page__badge">
              <span className="login-page__badge-dot" />
              <span>{intl.get("brand.privateGlobalBanking")}</span>
            </div>

            <h2 className="login-page__headline">
              {intl.get("brand.headlineLine1")}
              <br />
              <span>{intl.get("brand.headlineLine2")}</span>
              <br />
              {intl.get("brand.headlineLine3")}
            </h2>

            <div className="login-page__divider" />

            <p className="login-page__description">
              {intl.get("brand.description")}
            </p>
          </div>
        ) : null}

        <div className="login-page__brand-footer-group">
          <LanguageSelector className="login-page__language-select" />
        </div>
      </aside>

      <main className="login-page__main">
        <div className="login-page__main-glow" aria-hidden="true" />
        <div className="login-page__main-vignette" aria-hidden="true" />

        {showMobileLogo ? (
          <Link to="/auth" className="login-page__mobile-logo lg:hidden" aria-label={brandLabel}>
            <CompanyLogo
              forceValut={forceValutLogo}
              onDark
              variant="gold"
              width={120}
              height={28}
            />
          </Link>
        ) : null}

        <div className="login-page__form-shell">
          <Switch>
            <Route exact path={path}>
              <Login />
            </Route>
            <Route path={`${path}/verification-code`}>
              <VerificationCodeLogin />
            </Route>
            <Route path={`${path}/reset-password`}>
              <ResetPassword />
            </Route>
            <Route path={`${path}/forgot-password`}>
              <ForgotPassword />
            </Route>
            <Route path={`${path}/my-first-access`}>
              <MyFirstAccess />
            </Route>
            <Route path={`${path}/accepted-terms`}>
              <AcceptTerms />
            </Route>
            <Route path={`${path}/*`}>
              <Redirect to={path} />
            </Route>
          </Switch>
        </div>
      </main>

      {footer}

      <GlobalStyle />
      <PrivacyPolicyModal open={openPrivacyPolicy} onClose={handleClose} />
    </div>
  );
};
